/**
 * The Hangar panel model (M6 spec §4 "Switching and ferrying", "Insurance"; plan P27): the player's owned ships,
 * those docked at this station first, each with the four hangar actions and why each is on or off.
 *
 * - **Switch** to a ship docked here (not the one being flown).
 * - **Ferry** a ship elsewhere here: a real trip (it can be attacked). Not while it is in the transfer service, lost,
 *   flown, or already ferrying here.
 * - **Transfer** a ship elsewhere here through the paid transfer service (delayed, abstract). The worker needs it
 *   docked with an empty hold; the fee is per jump (fleet.json `transfer`, at least `minFee`), from the company
 *   account or the wallet.
 * - **Insure** (per ship): turning it on needs the M2 lease owned (fleet.json `insurance.requiresLeaseOwned`);
 *   dropping it is always allowed. The premium is max(minPerDay, dailyRate × insured value) per faction day, the
 *   insured value being the hull value × the hull's `valueMult` (M9 CH-8, `insuredValueOf`). A claimed ship can't
 *   be insured on its first ferry home (unregistered until it docks).
 * - **Claimed hulls** (M9 CH-12b): the row says "claimed", lists its flaws (`flawsOf`: bits 12–15 are the First Rush
 *   edge, never counted as a flaw) and the edge, and offers at a yard:
 *   - **Fix flaw**, one button per flaw: the goods from `flawFixQuote`, refused with `FLAW_FIX_REFUSAL_TEXT` (no
 *     yard, the hold lacks goods). Through the fleet service's `fixHullFlaw`.
 *   - **Scrap**, both `scrapQuote` forms: credits, or the hull's own parts to the hold (off with `cargoFull`'s words
 *     when the hold can't take them). Claimed ships only, through `scrapClaimedShip`.
 *   Off a yard, for a ship not docked here, or for one in a wing group (`inWingGroup`; the worker refuses `busy`),
 *   both are off with the reason (`hulks.json text.hangar`).
 *
 * - **In cradle** (M10 P5a11a): a ship riding in its L's cradle (`switch.ts cradleOf`) reads "In cradle"; switching,
 *   ferrying and the transfer service are off (it is boarded through its carrier, never from a station).
 *
 * - **History** (M10 P5a3c): the row lists the hull's last `HANGAR_HISTORY_ROWS` (3) history events, newest first
 *   ("Day 12: bought at Ceres Yard"), from the Career's hangar entry (`OwnedShip.history`, P5a3b). None recorded: none.
 *
 * - **Leased** (CL-7): while you fly the leased Kestrel (not a fleet ship) and the lease isn't owned, a line lists it
 *   as leased, so the Hangar never reads "no ships" while you fly one (panels.json `labels.hangar`).
 *
 * Pure.
 */
import { goodsBlockHoldT, hasHoldMult } from '../../../career/hullFit'
import { GOOD_NAMES, cargoFreeT } from '../../../career/ops'
import type { GoodId, Goods } from '../../../career/parts'
import type { Career, HullEvent, HullEventKind } from '../../../career/types'
import { ownedShip } from '../../../fleet-link/hangar'
import { cradleOf } from '../../../fleet-link/switch'
import { hulksConfig, type HulksConfig } from '../../../hulks/data'
import { edgeOf, flawsOf, valueMultOf } from '../../../hulks/flaws'
import { openHulkClaims } from '../../../hulks/kit'
import { FLAW_FIX_REFUSAL_TEXT, flawFixMissing, flawFixQuote } from '../../../hulks/restore'
import { scrapQuote } from '../../../hulks/scrap'
import type { FleetShipView, ShipStatusName } from '../../../strategic/types'
import { fillTokens } from '../../../story-link/world'
import { formatCr } from '../../format'
import { insuredValueOf, panelCatalog, type PanelCatalog } from './catalog'

export interface HangarInput {
  stationId: string
  ships: readonly FleetShipView[]
  /** The fleet id the player flies (`career.flownShip`), or null for the lease ship. */
  flownShip: string | null
  /** The lease is owned (per-ship insurance is open). */
  ownershipUnlocked: boolean
  soleTrader: boolean
  wallet: number
  /** Where a ship is, in words (defaults to its station or system id). */
  placeName?: (ship: Pick<FleetShipView, 'stationId' | 'systemId'>) => string
  /** M9: this station has a yard (flaw fixes and scrapping; the strategic view's shipyard). Default false. */
  yard?: boolean
  /** M9: the running Career (its hold pays fixes and takes scrapped parts; its hangar entries carry the flaws). */
  career?: Career | null
}

export type HangarActionId = 'switch' | 'ferry' | 'transfer' | 'insure'

/** Every hangar button: the four ship actions, and a claimed hull's flaw fix and two scrap forms (M9). */
export type HangarActionKind = HangarActionId | 'fixFlaw' | 'scrapCredits' | 'scrapGoods' | 'abandonClaim'

export interface HangarAction {
  id: HangarActionKind
  label: string
  enabled: boolean
  /** Why it is off ('' when on). */
  reason: string
}

/** One flaw on a hangar row, with its fix at a yard (M9). */
export interface HangarFlawRow {
  /** The flaw's list index (`hulks.json flaws[i]`, bit i): what `fixHullFlaw` takes. */
  index: number
  /** "Buckled spaceframe (hull −15%)". */
  text: string
  fix: HangarAction
}

export interface HangarRow {
  id: string
  name: string
  classLabel: string
  status: string
  where: string
  here: boolean
  flown: boolean
  insured: boolean
  captain: string
  actions: Record<HangarActionId, HangarAction>
  /** M9: a claimed hull (a hulk the player made flightworthy). */
  claimed: boolean
  /** M9: false while a claimed ship is on its first ferry home (unregistered until it docks). */
  registered: boolean
  /** M9: the hull's flaws, each with its yard fix (empty for a pristine hull). */
  flaws: HangarFlawRow[]
  /** M9: the First Rush pattern edge, in words, or null. */
  edge: string | null
  /**
   * M9: "Worth 72% of a new hull" for a flawed hull (its flaws' `valueMultOf`), and, when it insures for less (CH-8b:
   * a claimed ship insures at what was put in), "; insures for 18,450 cr, what was put in". Null for a pristine hull
   * insured at its value.
   */
  value: string | null
  /** M9: the two scrap forms of a claimed ship (null for any other ship). */
  scrap: { credits: HangarAction; goods: HangarAction } | null
  /** M10 P5a3c: the hull's last `HANGAR_HISTORY_ROWS` history events in words, newest first (empty: none recorded). */
  history: string[]
}

/** PF-2: one open hulk claim, abandonable from the hangar without flying to the hulk. */
export interface HangarClaimRow {
  /** The hulk key (`FleetService.closeHulkClaim` takes it). */
  key: string
  /** "Gunship: kit 2 / 14 delivered". */
  text: string
  abandon: HangarAction
}

export interface HangarModel {
  here: HangarRow[]
  away: HangarRow[]
  /** PF-2: the Career's open hulk claims (empty without a Career or a claim). */
  claims: HangarClaimRow[]
  /** PF-2: the claims section's title. */
  claimsHeader: string
  /**
   * CL-7 (D-1158 Q5): "Kestrel · leased from Tethys Crew Finance · you're flying it" while you fly the leased hull
   * (no fleet ship flown and the lease not owned), else null. The lease hull is no fleet ship, so it gets this line.
   */
  leased: string | null
  /** The leased line's section heading ("Flying now"). */
  leasedHeader: string
  /** "No other ships yet. Ships you buy appear here." when the fleet is empty. */
  empty: string | null
  transferNote: string
}

const STATUS_LABEL: Readonly<Record<ShipStatusName, string>> = Object.freeze({
  docked: 'Docked',
  working: 'Working',
  travelling: 'Travelling',
  ferrying: 'Ferrying',
  transfer: 'In transfer',
  striking: 'Striking',
  idle: 'Idle',
  lost: 'Lost',
})

/** The hangar's claimed-hull words (plain, no counters). */
export const HANGAR_TEXT = Object.freeze({
  claimed: 'claimed',
  unregistered: 'Not registered yet: it registers when it docks.',
  unregisteredStatus: 'Unregistered until it docks',
  notHere: 'Only ships docked here.',
  notDocked: 'The ship must be docked.',
  flying: 'You are flying it.',
  scrapNotYard: 'Only a station with a yard can scrap a ship.',
  holdNotEmpty: 'The hold must be empty.',
  worthless: 'Worth nothing as scrap.',
  missing: 'Missing:',
})

/** M10 P5a3c: how many history events a hangar row shows (the newest). */
export const HANGAR_HISTORY_ROWS = 3

/** M10 P5a3c: a hull event's verb on a hangar row. */
export const HULL_EVENT_TEXT: Readonly<Record<HullEventKind, string>> = Object.freeze({
  bought: 'bought',
  renamed: 'renamed',
  boarded: 'boarded',
  repaired: 'repaired',
  claimed: 'claimed',
})

/** M10 P5a3c: "Day 12: bought at Ceres Yard" (the station in words through `placeName`, else its id). */
export function hullEventText(e: HullEvent, placeName?: HangarInput['placeName']): string {
  const head = `Day ${e.day}: ${HULL_EVENT_TEXT[e.kind]}`
  if (e.stationId === undefined) return head
  const where = placeName?.({ stationId: e.stationId, systemId: '' }) ?? e.stationId
  return where === '' ? head : `${head} at ${where}`
}

/** M10 P5a3c: the last `HANGAR_HISTORY_ROWS` events of hangar entry `shipId`, newest first. */
function historyRows(shipId: string, input: HangarInput): string[] {
  const h = input.career == null ? undefined : ownedShip(input.career, shipId)?.history
  if (h === undefined) return []
  const out: string[] = []
  for (let i = h.length - 1; i >= 0 && out.length < HANGAR_HISTORY_ROWS; i--) out.push(hullEventText(h[i], input.placeName))
  return out
}

/**
 * The insurance premium per faction day of `hull`: max(minPerDay, dailyRate × insured value), the insured value being
 * the hull value × `valueMult` (M9 CH-8, `insuredValueOf`; 1 for a bought ship).
 */
export function premiumPerDay(hull: string, cat: PanelCatalog = panelCatalog(), valueMult = 1): number {
  const offer = cat.hullById.get(hull)
  const v = offer === undefined ? 0 : insuredValueOf(offer, valueMult)
  const p = cat.insurance.dailyRate * v
  return p > cat.insurance.minPerDay ? p : cat.insurance.minPerDay
}

/** M10 P5a6b H3: the hold tonnes scrap parts take on the flown hull (`applyScrap`'s check; the raw `goodsT` today). */
function scrapNeedT(career: Career | null | undefined, goods: Goods, goodsT: number): number {
  if (career == null || !hasHoldMult(career.ship.defId)) return goodsT
  return goodsBlockHoldT(career.ship.defId, goods)
}

/** M10 P5a11a: the status line and the reason for a ship riding in a cradle. */
export const CRADLE_TEXT = { status: 'In cradle', reason: 'In a cradle: board it through its carrier.' } as const

/** M10 P5a11a: true when `ship` rides in one of the player's cradles. */
function inCradle(ship: Pick<FleetShipView, 'id'>, input: Pick<HangarInput, 'career'>): boolean {
  return input.career != null && cradleOf(input.career, ship.id) !== null
}

function on(id: HangarActionKind, label: string): HangarAction {
  return { id, label, enabled: true, reason: '' }
}

function off(id: HangarActionKind, label: string, reason: string): HangarAction {
  return { id, label, enabled: false, reason }
}

export function switchAction(ship: FleetShipView, input: HangarInput): HangarAction {
  const label = 'Switch'
  if (ship.id === input.flownShip) return off('switch', label, 'You are flying it.')
  if (inCradle(ship, input)) return off('switch', label, CRADLE_TEXT.reason)
  if (ship.stationId !== input.stationId) return off('switch', label, 'Only ships docked here.')
  if (ship.status !== 'docked') return off('switch', label, `${STATUS_LABEL[ship.status]}: not docked.`)
  return on('switch', label)
}

export function ferryAction(ship: FleetShipView, input: HangarInput): HangarAction {
  const label = 'Ferry here'
  if (ship.id === input.flownShip) return off('ferry', label, 'You are flying it.')
  if (inCradle(ship, input)) return off('ferry', label, CRADLE_TEXT.reason)
  if (ship.status === 'lost') return off('ferry', label, 'Lost.')
  if (ship.status === 'transfer') return off('ferry', label, 'In the transfer service.')
  if (ship.stationId === input.stationId && ship.status === 'docked') return off('ferry', label, 'Already here.')
  if (ship.status === 'ferrying' && ship.order.target === input.stationId) return off('ferry', label, 'On its way here.')
  return on('ferry', label)
}

export function transferAction(ship: FleetShipView, input: HangarInput, cat: PanelCatalog = panelCatalog()): HangarAction {
  const label = 'Transfer here'
  if (ship.id === input.flownShip) return off('transfer', label, 'You are flying it.')
  if (inCradle(ship, input)) return off('transfer', label, CRADLE_TEXT.reason)
  if (ship.stationId === input.stationId && ship.status === 'docked') return off('transfer', label, 'Already here.')
  if (ship.status !== 'docked') return off('transfer', label, 'The ship must be docked.')
  if (ship.cargo !== null) return off('transfer', label, 'The hold must be empty.')
  const min = cat.transfer.minFee
  if (input.soleTrader && !(input.wallet >= min)) return off('transfer', label, `Not enough credits: at least ${formatCr(min)}.`)
  return on('transfer', label)
}

export function insureAction(ship: FleetShipView, input: HangarInput, cat: PanelCatalog = panelCatalog()): HangarAction {
  if (ship.status === 'lost') return off('insure', 'Insure', 'Lost.')
  if (ship.insured) return on('insure', 'Drop insurance')
  const label = `Insure (${formatCr(Math.round(premiumPerDay(ship.hull, cat, ship.valueMult ?? 1)))}/day)`
  // A claimed ship registers when it docks (the worker does it on the command), so only its first ferry refuses.
  if (ship.registered === false && ship.status !== 'docked') return off('insure', label, HANGAR_TEXT.unregistered)
  if (cat.insurance.requiresLeaseOwned && !input.ownershipUnlocked) return off('insure', label, 'Own the lease first.')
  return on('insure', label)
}

// ---------------------------------------------------------------------------------------------
// M9: claimed hulls (flaws, fixes, scrap)
// ---------------------------------------------------------------------------------------------

/** "8 Hull Plating, 2 Electronics" (whole units, in the goods' order). */
export function goodsText(goods: Readonly<Goods>): string {
  const parts: string[] = []
  for (const [g, n] of Object.entries(goods) as [GoodId, number | undefined][]) {
    if (n !== undefined && n > 0) parts.push(`${Math.ceil(n - 1e-9)} ${GOOD_NAMES[g]}`)
  }
  return parts.join(', ')
}

/** "+5%" / "−15%". */
export function signedPct(mult: number): string {
  const p = Math.round((mult - 1) * 100)
  return p > 0 ? `+${p}%` : `−${Math.abs(p)}%`
}

/** The ship's flaws bitmask: the Career's hangar entry (what a fix and a scrap check), else the worker's view. */
function flawBitsOf(ship: FleetShipView, career: Career | null | undefined): number {
  const entry = career == null ? null : ownedShip(career, ship.id)
  return entry?.flaws ?? ship.flaws ?? 0
}

/** The ship def of the ship: the Career's entry, else the hull's `ships.json` def. */
function defOf(ship: FleetShipView, input: HangarInput, cat: PanelCatalog): string {
  const entry = input.career == null ? null : ownedShip(input.career, ship.id)
  return entry?.defId ?? cat.hullById.get(ship.hull)?.shipDef ?? ship.hull
}

/** Why a yard job on `ship` can't happen here, or null (docked here, or the flown ship, which is here with the pilot). */
function yardPlaceRefusal(ship: FleetShipView, input: HangarInput): string | null {
  if (ship.id === input.flownShip) return null
  if (ship.stationId !== input.stationId) return HANGAR_TEXT.notHere
  if (ship.status !== 'docked') return HANGAR_TEXT.notDocked
  return null
}

/** The *Fix* button of flaw `index` on `ship`: CH-10's quote and refusals (the service re-checks on the Career). */
export function fixFlawAction(
  ship: FleetShipView,
  index: number,
  input: HangarInput,
  cat: PanelCatalog = panelCatalog(),
  cfg: HulksConfig = hulksConfig(),
): HangarAction {
  const q = flawFixQuote({ defId: defOf(ship, input, cat), flaws: flawBitsOf(ship, input.career) }, index, cfg)
  if (!q.ok) return off('fixFlaw', 'Fix', FLAW_FIX_REFUSAL_TEXT[q.reason])
  const label = `Fix (${goodsText(q.quote.goods)})`
  const place = yardPlaceRefusal(ship, input)
  if (place !== null) return off('fixFlaw', label, place)
  if (input.yard !== true) return off('fixFlaw', label, FLAW_FIX_REFUSAL_TEXT.notYard)
  const career = input.career ?? null
  if (career === null || ownedShip(career, ship.id) === null) return off('fixFlaw', label, FLAW_FIX_REFUSAL_TEXT.noShip)
  const missing = flawFixMissing(career, q.quote.goods)
  if (Object.keys(missing).length > 0) return off('fixFlaw', label, `${FLAW_FIX_REFUSAL_TEXT.goods} ${HANGAR_TEXT.missing} ${goodsText(missing)}.`)
  return on('fixFlaw', label)
}

/** The two *Scrap* buttons of a claimed `ship` (CH-11's quote; the worker re-checks the yard, the dock and the hold). */
export function scrapActions(ship: FleetShipView, input: HangarInput, cfg: HulksConfig = hulksConfig()): { credits: HangarAction; goods: HangarAction } {
  const q = scrapQuote(ship.hull, flawBitsOf(ship, input.career), cfg)
  const creditsLabel = `Scrap for ${formatCr(q.credits)}`
  const goodsLabel = `Scrap for parts (${goodsText(q.goods)})`
  let reason = ship.id === input.flownShip ? HANGAR_TEXT.flying : yardPlaceRefusal(ship, input)
  if (reason === null && input.yard !== true) reason = HANGAR_TEXT.scrapNotYard
  // The worker refuses a grouped ship `busy` (an escort, or the ship an escort follows): say why before the click.
  if (reason === null && ship.inWingGroup === true) reason = cfg.text.hangar.scrapInWingGroup
  if (reason === null && ship.cargo !== null) reason = HANGAR_TEXT.holdNotEmpty
  if (reason !== null) return { credits: off('scrapCredits', creditsLabel, reason), goods: off('scrapGoods', goodsLabel, reason) }
  const credits = q.credits > 0 ? on('scrapCredits', creditsLabel) : off('scrapCredits', creditsLabel, HANGAR_TEXT.worthless)
  const free = input.career == null ? 0 : cargoFreeT(input.career)
  let goods: HangarAction
  if (Object.keys(q.goods).length === 0) goods = off('scrapGoods', goodsLabel, HANGAR_TEXT.worthless)
  else if (scrapNeedT(input.career, q.goods, q.goodsT) > free + 1e-6) {
    // `cargoFull`'s refusal, in words.
    goods = off('scrapGoods', goodsLabel, `Not enough cargo space: the parts need ${scrapNeedT(input.career, q.goods, q.goodsT).toFixed(1)} t, ${free.toFixed(1)} t free.`)
  } else goods = on('scrapGoods', goodsLabel)
  return { credits, goods }
}

function flawRows(ship: FleetShipView, bits: number, input: HangarInput, cat: PanelCatalog, cfg: HulksConfig): HangarFlawRow[] {
  const rows: HangarFlawRow[] = []
  for (let i = 0; i < cfg.flaws.length; i++) {
    if ((bits & (1 << i)) === 0) continue
    const f = cfg.flaws[i]
    rows.push({ index: i, text: `${f.name} (${cfg.text.stats[f.stat]} ${signedPct(f.mult)})`, fix: fixFlawAction(ship, i, input, cat, cfg) })
  }
  return rows
}

/**
 * The row's value line: what the hull is worth by its flaws (`valueMultOf`, never the insured factor), and what it
 * insures for when that is less (CH-8b, D-572: a claimed ship insures at what was put in, the worker's `valueMult`).
 */
function valueLine(ship: FleetShipView, bits: number, cat: PanelCatalog, cfg: HulksConfig): string | null {
  const worth = valueMultOf(bits, cfg)
  const insured = ship.valueMult ?? 1
  const offer = cat.hullById.get(ship.hull)
  const worthText = worth < 1 - 1e-6 ? `Worth ${Math.round(worth * 100)}% of a new hull` : null
  if (offer === undefined || insured >= worth - 1e-6) return worthText
  const cover = `${formatCr(insuredValueOf(offer, insured))}, what was put in`
  return worthText === null ? `Insures for ${cover}` : `${worthText}; insures for ${cover}`
}

function rowOf(ship: FleetShipView, input: HangarInput, cat: PanelCatalog, cfg: HulksConfig): HangarRow {
  const place = input.placeName?.(ship) ?? ship.stationId ?? ship.systemId
  const bits = flawBitsOf(ship, input.career)
  const edge = edgeOf(bits, cfg)
  const claimed = ship.claimed === true
  return {
    id: ship.id,
    name: ship.name,
    classLabel: cat.config.classLabels[ship.shipClass],
    status: inCradle(ship, input) ? CRADLE_TEXT.status : STATUS_LABEL[ship.status],
    where: place === null || place === '' ? 'In transit' : place,
    here: ship.stationId === input.stationId,
    flown: ship.id === input.flownShip,
    insured: ship.insured,
    captain: ship.captain === null ? 'No captain' : ship.captain.name,
    actions: {
      switch: switchAction(ship, input),
      ferry: ferryAction(ship, input),
      transfer: transferAction(ship, input, cat),
      insure: insureAction(ship, input, cat),
    },
    claimed,
    registered: ship.registered !== false,
    flaws: flawsOf(bits, cfg).length > 0 ? flawRows(ship, bits, input, cat, cfg) : [],
    edge: edge === null ? null : `First Rush pattern: ${edge.name} (${cfg.text.stats[edge.stat]} ${signedPct(edge.mult)})`,
    value: valueLine(ship, bits, cat, cfg),
    scrap: claimed ? scrapActions(ship, input, cfg) : null,
    history: historyRows(ship.id, input),
  }
}

export function hangarModel(input: HangarInput, cat: PanelCatalog = panelCatalog(), cfg: HulksConfig = hulksConfig()): HangarModel {
  const here: HangarRow[] = []
  const away: HangarRow[] = []
  for (const s of input.ships) {
    if (s.status === 'lost') continue
    const r = rowOf(s, input, cat, cfg)
    if (r.here) here.push(r)
    else away.push(r)
  }
  const t = cat.transfer
  const ct = cfg.text.claims
  const claims: HangarClaimRow[] = (input.career == null ? [] : openHulkClaims(input.career, cfg)).map((c) => ({
    key: c.key,
    text: fillTokens(ct.row, { hull: cfg.text.hulls[c.hull] ?? c.hull, delivered: String(c.delivered), needed: String(c.needed) }),
    abandon: on('abandonClaim', ct.abandon),
  }))
  const words = cat.config.labels.hangar
  return {
    here,
    away,
    claims,
    claimsHeader: ct.header,
    leased: input.flownShip === null && !input.ownershipUnlocked ? words.leased : null,
    leasedHeader: words.leasedHeader,
    empty: here.length + away.length === 0 ? words.empty : null,
    transferNote: `Transfer service: ${formatCr(t.feePerJump)} per jump, at least ${formatCr(t.minFee)}.`,
  }
}
